import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setAlertData } from "../../Redux/Screen/Action";

interface OdrStatusUpdateModalProps {
  open: boolean;
  onClose: () => void;
  currentCaseId: number | string | null | undefined;
  // Display-only. currentCaseId (pkCase) still drives the API call/logic below unchanged.
  currentCaseNumber?: number | string | null | undefined;
  currentStatus: string;
  onStatusUpdated?: (newStatus: string) => void;
}

const ODR_STATUSES = [
  { value: "INITIATED", label: "Initiated" },
  { value: "AWAITING_CONSENT", label: "Awaiting Consent" },
  { value: "TRIAGED", label: "Triaged" },
  { value: "NEGOTIATION", label: "Negotiation Phase" },
  { value: "MEDIATION", label: "Mediation Phase" },
  { value: "ARBITRATION", label: "Arbitration Phase" },
  { value: "NOTICE_EXPIRED", label: "Notice Expired" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ARBITRATION_INITIATED", label: "Arbitration Initiated" },
  { value: "WITHDRAWN", label: "Withdrawn" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "CLOSED_UNRESOLVED", label: "Closed Unresolved" },
  { value: "DEFAULT_AWARDED", label: "Default Awarded" },
];

const OdrStatusUpdateModal: React.FC<OdrStatusUpdateModalProps> = ({
  open,
  onClose,
  currentCaseId,
  currentCaseNumber,
  currentStatus,
  onStatusUpdated,
}) => {
  const dispatch = useDispatch();
  const [targetStatus, setTargetStatus] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setTargetStatus(event.target.value);
    setErrorMsg(null);
  };

  const handleTransition = async () => {
    if (!targetStatus) {
      setErrorMsg("Please select a target status.");
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const token = localStorage.getItem("token") || "";
      const userId = localStorage.getItem("x-nlp-userid") || "";
      
      const response = await axios.post(
        `/api/Odr/${currentCaseId}/transition-status`,
        {
          targetStatus: targetStatus,
          notes: notes,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-nlp-userid": userId,
          },
          // Same fix as Service/index.tsx's central client: no timeout meant a network stall left the
          // submit button spinning indefinitely (the finally block below can't run until the request
          // settles one way or the other).
          timeout: 30000,
        }
      );

      if (response.data?.success) {
        dispatch(
          setAlertData({
            alertMessage: `Case status transitioned to ${targetStatus} successfully.`,
            alertVariant: "success",
            openAlert: true,
          })
        );
        if (onStatusUpdated) {
          onStatusUpdated(response.data.currentStatus || targetStatus);
        }
        // Reset and close
        setTargetStatus("");
        setNotes("");
        onClose();
      }
    } catch (err: any) {
      console.error("Failed to transition status", err);
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An error occurred while transitioning status. Please verify if the transition is allowed.";
      setErrorMsg(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="odr-status-modal-title"
      aria-describedby="odr-status-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography variant="h6" sx={{ color: "white", fontWeight: 700, fontFamily: "Outfit" }}>
            ODR Status Update
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>
              {errorMsg}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 3, color: "#64748b" }}>
            Transition Case <strong>#{currentCaseNumber || currentCaseId}</strong> from current status{" "}
            <Chip
              label={currentStatus || "INITIATED"}
              size="small"
              sx={{ fontWeight: 700, bgcolor: "#f1f5f9", textTransform: "uppercase" }}
            />
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="odr-status-select-label">Select Target Status</InputLabel>
            <Select
              labelId="odr-status-select-label"
              id="odr-status-select"
              value={targetStatus}
              label="Select Target Status"
              onChange={handleStatusChange}
              sx={{ borderRadius: "12px" }}
            >
              {ODR_STATUSES.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label} ({status.value})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Transition Notes / Rationale"
            placeholder="Provide comments or legal rationale for this state transition..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        </Box>

        {/* Footer */}
        <Box sx={footerStyle}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              mr: 2,
              borderRadius: "12px",
              px: 3,
              textTransform: "none",
              fontFamily: "Outfit",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTransition}
            disabled={submitting || !targetStatus}
            sx={{
              bgcolor: "#283165",
              "&:hover": { bgcolor: "#1c234b" },
              borderRadius: "12px",
              px: 3,
              textTransform: "none",
              fontFamily: "Outfit",
              fontWeight: 600,
            }}
          >
            {submitting ? "Updating..." : "Transition Status"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "500px" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "20px",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
  outline: "none",
  overflow: "auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  bgcolor: "#283165",
  px: 3,
  py: 2.5,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  px: 3,
  py: 2.5,
  borderTop: "1px solid #e2e8f0",
};

export default OdrStatusUpdateModal;
